// CompareChart.tsx — Vergleich's overlay chart: net-wealth (Marktwert + Verrechnungskonto)
// series for every SELECTED scenario in one chart, each with a distinct color and an
// interactive toggle-chip legend (click to show/hide that scenario's line) — generalized
// from the original fixed-A/B-only version to an arbitrary number of overlaid series, one
// per lib/theme.ts's seriesColor(index) palette slot.
import { useCallback, useMemo, useState } from 'preact/hooks';
import type { AlignedData, Options } from 'uplot';
import type { SeriesPoint } from '@etf-cockpit/engine';
import { UplotBase } from './UplotBase.js';
import { formatCents, formatCentsCompact } from '../lib/format.js';
import { zeroLinePlugin, chartZoomPlugin } from '../lib/uplot-plugins.js';
import type { ZoomState } from '../lib/uplot-plugins.js';
import { measuredAxisSize } from '../lib/uplotAxis.js';
import { seriesColor } from '../lib/theme.js';
import { isSeriesVisible, toggleSeriesVisibility } from '../lib/seriesVisibility.js';
import type { SeriesVisibility } from '../lib/seriesVisibility.js';

export interface CompareChartSeries {
  /** Scenario id — the toggle-legend key, and what seriesColor(index-in-this-array)
   * colors. Stable across re-selections as long as this array's own order is stable. */
  id: string;
  label: string;
  data: SeriesPoint[];
}

export interface CompareChartProps {
  series: CompareChartSeries[];
}

function toUnixSeconds(iso: string): number {
  return Math.floor(new Date(`${iso}T00:00:00Z`).getTime() / 1000);
}

function netWealth(p: SeriesPoint): number {
  return p.valueCents + p.settlementCents;
}

/** Merges N possibly differently-lengthed/dated series onto one sorted, shared x-axis —
 * the N-series generalization of the old mergeOnXAxis(a, b). */
function mergeOnXAxis(series: CompareChartSeries[]): AlignedData {
  const byXPerSeries = series.map((s) => new Map(s.data.map((p) => [toUnixSeconds(p.date), netWealth(p)])));
  const xs = Array.from(new Set(byXPerSeries.flatMap((m) => Array.from(m.keys())))).sort((a, b) => a - b);
  const columns: (number | null)[][] = [xs];
  for (const byX of byXPerSeries) {
    columns.push(xs.map((x) => byX.get(x) ?? null));
  }
  return columns as unknown as AlignedData;
}

export function CompareChart({ series }: CompareChartProps) {
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  const [visibility, setVisibility] = useState<SeriesVisibility>({});

  const data = useMemo(() => mergeOnXAxis(series), [series]);

  const buildOptions = useCallback(
    (width: number): Options => {
      return {
        width,
        height: 220,
        padding: [8, 8, 0, 0],
        legend: { show: true },
        cursor: { drag: { x: true, y: true }, points: { size: 6 } },
        scales: {
          x: { time: true },
          y: { range: (_u, initMin, initMax) => [Math.min(0, initMin), Math.max(0, initMax)] },
        },
        axes: [
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
          },
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
            values: (_u, ticks) => ticks.map((t) => formatCentsCompact(t)),
            size: measuredAxisSize,
          },
        ],
        series: [
          { label: 'Datum' },
          ...series.map((s, i) => ({
            label: s.label,
            stroke: seriesColor(i),
            width: 2,
            spanGaps: true,
            show: isSeriesVisible(visibility, s.id),
            value: (_u: unknown, v: number | null) => (v == null ? '—' : formatCents(v)),
          })),
        ],
        plugins: [zeroLinePlugin(), chartZoomPlugin(setZoom)],
      };
    },
    [series, visibility, setZoom],
  );

  if (series.every((s) => s.data.length === 0)) {
    return <div class="empty-state">Keine Zeitreihen vorhanden.</div>;
  }

  return (
    <div class="chart-wrap">
      <div class="chart-legend" role="group" aria-label="Szenarien ein-/ausblenden">
        {series.map((s, i) => (
          <button
            key={s.id}
            type="button"
            class="legend-toggle"
            aria-pressed={isSeriesVisible(visibility, s.id)}
            onClick={() => setVisibility((v) => toggleSeriesVisibility(v, s.id))}
          >
            <span class="swatch" style={{ background: seriesColor(i) }} />
            {s.label}
          </button>
        ))}
      </div>
      <UplotBase data={data} buildOptions={buildOptions} height={220} />
      {zoom?.zoomed ? (
        <div class="chart-zoom-row">
          <button type="button" class="chart-zoom-reset" onClick={() => zoom.reset()}>
            Zoom zurücksetzen
          </button>
        </div>
      ) : null}
    </div>
  );
}
